// Brand colours are chosen by customers, so every surface that shows one works out readable
// text for it (WCAG relative luminance and contrast).
export const SWATCHES = [
  { name: 'Clay', hex: '#B4472B' }, { name: 'Ink', hex: '#1F2328' }, { name: 'Teal', hex: '#1F6F78' },
  { name: 'Plum', hex: '#6B3E75' }, { name: 'Gold', hex: '#8A6A12' }, { name: 'Forest', hex: '#2F6B4F' },
  { name: 'Harbour', hex: '#2B5F8A' }
];
export const DEFAULT_COLOR = '#B4472B';
const HEX = /^#[0-9a-fA-F]{6}$/;

export function validHex(value) {
  return HEX.test(String(value || ''));
}

export function normalHex(value, fallback) {
  const text = String(value || '').trim();
  const full = /^#?[0-9a-fA-F]{3}$/.test(text) ? '#' + text.replace('#', '').split('').map(c => c + c).join('') : text;
  const hex = full.charAt(0) === '#' ? full : '#' + full;
  return validHex(hex) ? hex.toUpperCase() : (fallback === undefined ? DEFAULT_COLOR : fallback);
}

function channels(hex) {
  const value = normalHex(hex);
  return [1, 3, 5].map(i => parseInt(value.slice(i, i + 2), 16));
}

export function luminance(hex) {
  const [r, g, b] = channels(hex).map(c => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrast(a, b) {
  const x = luminance(a);
  const y = luminance(b);
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}

// Text on a brand-coloured button: white when it reads, otherwise ink.
export function onColor(hex) {
  return contrast(hex, '#FFFFFF') >= 4.5 ? '#FFFFFF' : '#1F2328';
}

function mix(hex, target, amount) {
  const a = channels(hex);
  const b = channels(target);
  return '#' + a.map((c, i) => Math.round(c + (b[i] - c) * amount).toString(16).padStart(2, '0')).join('').toUpperCase();
}

// The brand colour darkened just enough to be readable as text on white.
export function textColor(hex) {
  let value = normalHex(hex);
  for (let step = 0; step < 12 && contrast(value, '#FFFFFF') < 4.5; step++) value = mix(value, '#1F2328', 0.18);
  return value;
}

export function softColor(hex, alpha) {
  const [r, g, b] = channels(hex);
  return 'rgba(' + r + ',' + g + ',' + b + ',' + (alpha === undefined ? 0.12 : alpha) + ')';
}

// CSS variables a branded public surface sets on its root element.
export function brandVars(hex) {
  const color = normalHex(hex);
  return { '--brand': color, '--brand-on': onColor(color), '--brand-text': textColor(color),
    '--brand-soft': softColor(color, 0.1), '--brand-line': softColor(color, 0.38) };
}

export function initials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '·';
  return (parts[0].charAt(0) + (parts.length > 1 ? parts[parts.length - 1].charAt(0) : '')).toUpperCase();
}
